import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useMediaHealth,
  type MediaCallPath,
  type MediaHealth,
} from "@/features/admin";
import { formatDuration } from "@/features/utils/numbers";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { StatCard } from "./components/stat-card";

const WINDOWS = [
  { hours: 24, label: "24 dernières heures" },
  { hours: 24 * 7, label: "7 derniers jours" },
  { hours: 24 * 30, label: "30 derniers jours" },
];

const PATHS: Record<MediaCallPath, string> = {
  trunk: "Trunk SIP",
  webhook: "Webhook Twilio",
  "": "Arrivée inconnue",
};

const seconds = (ms: number) =>
  `${(ms / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} s`;

const when = (t: number) =>
  new Date(t).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const BAD = "text-red-600 dark:text-red-400";

/**
 * The call drawn as a bar, each stretch without audio from the caller in red — the
 * "5 s of audio, 5 s of nothing" pattern reads at a glance, where a list of offsets does not.
 */
function GapTimeline({ media }: { media: MediaHealth }) {
  const total = Math.max(1, media.duration_ms);
  return (
    <div
      className="relative h-2 w-40 overflow-hidden rounded bg-emerald-500/40"
      title={media.gap_list
        .map((g) => `à ${seconds(g.at_ms)} : ${seconds(g.ms)} sans audio`)
        .join("\n")}
    >
      {media.gap_list.map((g) => (
        <div
          key={g.at_ms}
          className="absolute inset-y-0 bg-red-500"
          style={{
            left: `${(g.at_ms / total) * 100}%`,
            width: `${Math.max(0.5, (g.ms / total) * 100)}%`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Did the caller's audio reach us? One line per phone call, from the verdict each leg
 * leaves on its session when it ends (`metrics.media`).
 *
 * A gap here is not a caller being quiet — silence still arrives, 50 frames a second — but
 * no packet at all: the audio was lost upstream of our Asterisk (the caller's carrier,
 * Twilio, the network up to our host). Our own recording has the same holes, and the agent
 * only ever heard what was left.
 */
export function AdminMediaHealthPage() {
  const [hours, setHours] = useState(24);
  const [all, setAll] = useState(false);
  const { data, isLoading } = useMediaHealth(hours, all);
  const calls = data?.calls || [];

  return (
    <AdminPageLayout
      title="Audio des appels"
      description="Les appels où l'audio de l'appelant n'est pas arrivé jusqu'à nous"
      actions={[
        <div key="all" className="flex items-center gap-2">
          <Switch id="media-all" checked={all} onCheckedChange={setAll} />
          <Label htmlFor="media-all" className="text-sm">
            Tous les appels
          </Label>
        </div>,
        <Select
          key="window"
          value={String(hours)}
          onValueChange={(v) => setHours(Number(v))}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.hours} value={String(w.hours)}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      ]}
    >
      <p className="text-xs text-muted-foreground">
        Un trou, c'est une seconde ou plus sans aucun paquet de l'appelant — pas
        un silence, qui arrive lui aussi. L'audio s'est perdu avant notre
        Asterisk (opérateur de l'appelant, Twilio, réseau) : l'enregistrement a
        les mêmes trous et l'agent n'a entendu que le reste. Un appel est
        dégradé dès un trou de 2 s ou moins de 90 % des trames reçues.
      </p>

      <div className="grid grid-cols-3 gap-4">
        <StatCard
          title="Appels mesurés"
          value={data ? data.calls_measured.toLocaleString("fr-FR") : "—"}
          hint="Appels téléphoniques terminés sur la période"
          isLoading={isLoading}
        />
        <StatCard
          title="Audio de l'appelant dégradé"
          value={data ? data.calls_degraded.toLocaleString("fr-FR") : "—"}
          hint={
            data?.calls_measured
              ? `${Math.round((data.calls_degraded / data.calls_measured) * 100)} % des appels`
              : undefined
          }
          isLoading={isLoading}
          tone={data?.calls_degraded ? "bad" : "default"}
        />
        <StatCard
          title="Plus long trou"
          value={data ? seconds(data.max_gap_ms) : "—"}
          isLoading={isLoading}
          tone={(data?.max_gap_ms || 0) >= 2000 ? "bad" : "default"}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {all ? "Appels" : "Appels dégradés"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {calls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {isLoading
                ? "Chargement…"
                : all
                  ? "Aucun appel mesuré sur la période."
                  : "Aucun appel dégradé sur la période."}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Appelant</TableHead>
                  <TableHead className="text-right">Durée</TableHead>
                  <TableHead className="text-right">Reçu</TableHead>
                  <TableHead className="text-right">Trous</TableHead>
                  <TableHead>Audio de l'appelant</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {when(c.created_at)}
                    </TableCell>
                    <TableCell>
                      <Link
                        className="underline-offset-2 hover:underline"
                        to={`/admin/clients/${encodeURIComponent(c.client_id)}`}
                      >
                        {c.client_name || c.client_id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs">
                        {c.phone_number || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {PATHS[c.path] ?? c.path}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDuration(c.media.duration_ms / 1000)}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        c.media.ratio < 90 ? BAD : ""
                      }`}
                    >
                      {c.media.ratio} %
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums whitespace-nowrap ${
                        c.media.status === "degraded" ? BAD : ""
                      }`}
                    >
                      {c.media.gaps
                        ? `${c.media.gaps} · max ${seconds(c.media.max_gap_ms)}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <GapTimeline media={c.media} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        className="text-xs underline-offset-2 hover:underline"
                        to={`/${c.client_id}/calls/session/${c.id}`}
                      >
                        Ouvrir
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
